import {
    CheckCircleOutlined,
    DeleteOutlineOutlined,
    ExpandLessOutlined,
    ExpandMoreOutlined,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addMinutes, formatRecordDate, formatRecordDateTime } from '../../../library/dateTimeHelper';
import { AppointmentStatus } from '../../../library/models/appointment';
import { IInvoice } from '../../../library/models/invoice';
import {
    bookableSessions,
    IPatientPurchase,
    IPurchaseDetail,
    isUsable,
    sessionsUsed,
} from '../../../library/models/patientPurchase';
import {
    discountPercent,
    formatPrice,
    PURCHASE_STATUS_COLOURS,
    PURCHASE_STATUS_LABELS,
    SERVICE_TYPE_LABELS,
} from '../../../library/purchaseDisplay';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { AdjustSessionsUsedBeforeDialog } from './AdjustSessionsUsedBeforeDialog';
import { PurchaseInvoiceActions } from './PurchaseInvoiceActions';
import { RecordSessionDialog } from './RecordSessionDialog';
import {
    actionRowStyles,
    allBookedHintStyles,
    cancelButtonStyles,
    cardHeaderStyles,
    cardMutedStyles,
    cardStyles,
    chipRowStyles,
    metaRowStyles,
    notesStyles,
    priceBlockStyles,
    priceStyles,
    progressBarStyles,
    progressLabelStyles,
    progressRowStyles,
    remainingStyles,
    scheduledLineStyles,
    serviceNameStyles,
    sessionDateStyles,
    sessionListStyles,
    sessionRowStyles,
    sessionTherapistStyles,
    statusChipStyles,
    strikePriceStyles,
} from './purchaseCard.styles';
import { localised } from '../../../library/models/localisedText';

/**
 * How each state reads, and how it looks.
 *
 * A no-show is warning rather than error: the patient still has the session, so nothing was
 * lost -- what it flags is a pattern worth noticing, not a failure.
 */
const STATUS_LABELS: Record<AppointmentStatus, string> = {
    SCHEDULED: ResourcesIds.APPOINTMENT_STATUS_SCHEDULED,
    HELD: ResourcesIds.APPOINTMENT_STATUS_HELD,
    NO_SHOW: ResourcesIds.APPOINTMENT_STATUS_NO_SHOW,
    CANCELLED: ResourcesIds.APPOINTMENT_STATUS_CANCELLED,
    REQUESTED: ResourcesIds.APPOINTMENT_STATUS_REQUESTED,
    DECLINED: ResourcesIds.APPOINTMENT_STATUS_DECLINED,
};

/** A request is `info`: nothing is wrong with it, it is simply waiting for an answer. */
const STATUS_COLOURS: Record<AppointmentStatus, 'default' | 'success' | 'warning' | 'info'> = {
    SCHEDULED: 'default',
    HELD: 'success',
    NO_SHOW: 'warning',
    CANCELLED: 'default',
    REQUESTED: 'info',
    DECLINED: 'default',
};

interface PurchaseCardProps {
    purchase: IPatientPurchase;
    /** Refreshes the patient's list, whose remaining counts every action here changes. */
    onChanged: () => void;
    /**
     * This purchase's live invoice, if it has one.
     *
     * Passed in rather than fetched here: a patient with six purchases would otherwise open
     * with six requests for a list the page already has in one.
     */
    invoice?: IInvoice;
    /** Prefills the invoice's client, which is usually but not always who bought. */
    patientName: string;
    patientEmail?: string;
    /** Refreshes the invoice list. Separate from `onChanged`: invoicing changes no counts. */
    onInvoiceChanged: () => void;
    /**
     * Whether the caller may record sessions used before Carefull -- an administrator, which
     * the API checks again. The count itself is shown to everybody either way.
     */
    canAdjustUsedBefore?: boolean;
}

/**
 * One thing the patient bought.
 *
 * A package leads with what is left of it, because that is the question a therapist opens
 * this page to answer. A single session has no remainder worth a progress bar, so it says
 * held or not held instead.
 *
 * The session history is fetched only when it is opened. A patient with a dozen purchases
 * would otherwise pay for a dozen extra requests to render a page that shows none of them.
 */
export const PurchaseCard = (props: PurchaseCardProps): JSX.Element => {
    const { purchase, onChanged } = props;
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const [expanded, setExpanded] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isAdjusting, setIsAdjusting] = useState(false);

    const {
        data: detail,
        isLoading: isLoadingSessions,
        refetch: refetchSessions,
    } = useReactQuery<IPurchaseDetail>(services.patientPurchases.getPurchase(purchase.id), {
        enabled: expanded,
    });

    // A walk-in: an appointment created straight into HELD, because it already happened.
    // The booked-then-resolved path lives on the therapist's day view instead.
    const recordSession = useReactMutation(
        services.appointments.createAppointmentMutation()
    );
    // A real delete, for a row that should never have been written down. An appointment
    // that will genuinely not happen is cancelled on the day view instead, so it stays
    // visible.
    const deleteSession = useReactMutation(
        services.appointments.deleteAppointmentMutation()
    );
    const cancelPurchase = useReactMutation(services.patientPurchases.cancelPurchaseMutation());

    const isPackage = purchase.serviceType === 'PACKAGE';
    const used = sessionsUsed(purchase);
    const discount = discountPercent(purchase.listPrice ?? 0, purchase.discountAmount ?? 0);

    /**
     * Recording writes a *new* session, so it asks what a booking form asks: the remainder
     * less what is already in the diary. `isUsable` alone -- which is only "not cancelled,
     * not used up" -- offered the button for a purchase whose last session was already
     * booked, and the API refused every press of it with "every session has been used",
     * which was not even true: none had been used, one was booked.
     */
    const canRecord = isUsable(purchase) && bookableSessions(purchase) > 0;

    /** Usable, but every session left is spoken for by an appointment. */
    const allBooked = isUsable(purchase) && bookableSessions(purchase) === 0;

    /** Both the count on this card and the history behind it move when a session lands. */
    const refreshAfterSessionChange = (): void => {
        onChanged();

        if (expanded) {
            refetchSessions();
        }
    };

    /**
     * The start time comes from the dialog; the therapist still defaults on the server to
     * whoever is signed in.
     */
    const handleRecordSession = (startedAt: string): void => {
        recordSession.mutate(
            {
                patientId: purchase.patientId,
                therapistId: purchase.therapistId,
                purchaseId: purchase.id,
                startsAt: startedAt,
                // The length is the purchase's own, snapshotted when it was sold. Asking
                // for it again could only produce a second answer that disagrees.
                endsAt: addMinutes(startedAt, purchase.durationMinutes),
                status: 'HELD',
            },
            {
                onSuccess: () => {
                    showSnackbar(
                        translate(ResourcesIds.PURCHASE_RECORD_SESSION_SUCCESS),
                        'success'
                    );
                    setIsRecording(false);
                    refreshAfterSessionChange();
                },
                onError: () =>
                    showSnackbar(translate(ResourcesIds.PURCHASE_RECORD_SESSION_ERROR), 'error'),
            }
        );
    };

    const handleDeleteSession = (appointmentId: number): void => {
        deleteSession.mutate(appointmentId, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.PURCHASE_UNDO_SESSION_SUCCESS), 'success');
                refreshAfterSessionChange();
            },
            onError: () =>
                showSnackbar(translate(ResourcesIds.PURCHASE_UNDO_SESSION_ERROR), 'error'),
        });
    };

    const handleUsedBeforeSaved = (): void => {
        showSnackbar(translate(ResourcesIds.PURCHASE_USED_BEFORE_SUCCESS), 'success');
        setIsAdjusting(false);
        onChanged();
    };

    const handleCancel = (): void => {
        cancelPurchase.mutate(purchase.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.PURCHASE_CANCEL_SUCCESS), 'success');
                onChanged();
            },
            onError: () => showSnackbar(translate(ResourcesIds.PURCHASE_CANCEL_ERROR), 'error'),
        });
    };

    return (
        <Paper
            elevation={0}
            sx={purchase.status === 'ACTIVE' ? cardStyles : cardMutedStyles}
        >
            <Box sx={cardHeaderStyles}>
                <Box>
                    <Typography variant="h6" sx={serviceNameStyles}>
                        {localised(purchase.serviceNameRo, purchase.serviceNameEn, i18n.language)}
                    </Typography>

                    <Box sx={chipRowStyles}>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={translate(SERVICE_TYPE_LABELS[purchase.serviceType])}
                        />
                        <Chip
                            size="small"
                            color={PURCHASE_STATUS_COLOURS[purchase.status]}
                            label={translate(PURCHASE_STATUS_LABELS[purchase.status])}
                        />
                    </Box>

                    <Box sx={metaRowStyles}>
                        {/* The date of the sale, whichever kind it is -- `assignedOn` has
                            never been anything else. It used to be labelled as the day a
                            single session was given, which was a delivery the card could
                            not know had happened: it said so beside its own "not held yet"
                            chip on every sale where the session was recorded separately.
                            When a session was given is what the chip and the list below
                            answer, from the appointments that actually exist. */}
                        <span>
                            {translate(
                                isPackage
                                    ? ResourcesIds.PURCHASE_ASSIGNED_ON
                                    : ResourcesIds.PURCHASE_SOLD_ON,
                                { date: formatRecordDate(purchase.assignedOn, i18n) }
                            )}
                        </span>
                        {purchase.validUntil && (
                            <span>
                                {translate(ResourcesIds.PURCHASE_VALID_UNTIL, {
                                    date: formatRecordDate(purchase.validUntil, i18n),
                                })}
                            </span>
                        )}
                    </Box>
                </Box>

                <Box sx={priceBlockStyles}>
                    <Typography variant="h6" sx={priceStyles}>
                        {formatPrice(purchase.price ?? 0)} RON
                    </Typography>

                    {/* Only when something was actually taken off: a struck-through price
                        identical to the one beside it reads as a rendering bug. */}
                    {(purchase.discountAmount ?? 0) > 0 && (
                        <Typography component="div" sx={strikePriceStyles}>
                            {formatPrice(purchase.listPrice ?? 0)} RON
                            {discount > 0 && ` (-${discount}%)`}
                        </Typography>
                    )}

                    {isPackage && purchase.totalSessions > 0 && (
                        <Typography component="div" sx={sessionTherapistStyles}>
                            {translate(ResourcesIds.PURCHASE_PER_SESSION, {
                                price: formatPrice((purchase.price ?? 0) / purchase.totalSessions),
                            })}
                        </Typography>
                    )}
                </Box>
            </Box>

            {isPackage ? (
                <Box sx={progressRowStyles}>
                    <Box sx={progressLabelStyles}>
                        <Typography sx={remainingStyles}>
                            {translate(ResourcesIds.PURCHASE_SESSIONS_REMAINING, {
                                remaining: purchase.remainingSessions,
                                total: purchase.totalSessions,
                            })}
                        </Typography>
                    </Box>

                    {/* Two different questions. The remainder is what the clinic still owes
                        work for -- the money's figure, so a booking that has not happened
                        does not reduce it. How many of those are already in the diary is
                        what tells a therapist whether there is anything left to offer. */}
                    {purchase.scheduledSessions > 0 && (
                        <Typography component="div" sx={scheduledLineStyles}>
                            {translate(ResourcesIds.PURCHASE_SESSIONS_BOOKED, {
                                booked: purchase.scheduledSessions,
                            })}
                        </Typography>
                    )}

                    {/* Shown to everybody who sees the card, so a count an administrator
                        brought across from the old system is never silently part of the
                        remainder above. */}
                    {purchase.sessionsUsedBefore > 0 && (
                        <Typography component="div" sx={scheduledLineStyles}>
                            {translate(ResourcesIds.PURCHASE_USED_BEFORE, {
                                count: purchase.sessionsUsedBefore,
                            })}
                        </Typography>
                    )}

                    <LinearProgress
                        variant="determinate"
                        value={(used / purchase.totalSessions) * 100}
                        sx={progressBarStyles}
                    />
                </Box>
            ) : (
                <Box sx={progressRowStyles}>
                    <Chip
                        size="small"
                        icon={used > 0 ? <CheckCircleOutlined /> : undefined}
                        color={used > 0 ? 'success' : 'default'}
                        variant={used > 0 ? 'filled' : 'outlined'}
                        label={translate(
                            used > 0
                                ? ResourcesIds.PURCHASE_SESSION_HELD
                                : ResourcesIds.PURCHASE_SESSION_NOT_HELD
                        )}
                    />
                </Box>
            )}

            {purchase.notes && (
                <Typography variant="body2" sx={notesStyles}>
                    {purchase.notes}
                </Typography>
            )}

            <Box sx={actionRowStyles}>
                {canRecord && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => setIsRecording(true)}
                        disabled={recordSession.isPending}
                    >
                        {translate(ResourcesIds.PURCHASE_RECORD_SESSION)}
                    </Button>
                )}

                {/* Says why, rather than leaving a button to vanish -- the same reason
                    the invoice actions explain the month running out. Silence here reads
                    as a bug, and the therapist would go looking for the button instead of
                    going to the day view, which is where the answer is. */}
                {allBooked && (
                    <Typography variant="caption" sx={allBookedHintStyles}>
                        {translate(ResourcesIds.PURCHASE_ALL_BOOKED)}
                    </Typography>
                )}

                <Button
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    endIcon={expanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
                >
                    {translate(
                        expanded
                            ? ResourcesIds.PURCHASE_HIDE_SESSIONS
                            : ResourcesIds.PURCHASE_SHOW_SESSIONS
                    )}
                </Button>

                {props.canAdjustUsedBefore && purchase.status !== 'CANCELLED' && (
                    <Button size="small" onClick={() => setIsAdjusting(true)}>
                        {translate(ResourcesIds.PURCHASE_USED_BEFORE_ADJUST)}
                    </Button>
                )}

                {purchase.status !== 'CANCELLED' && (
                    <Button
                        size="small"
                        color="error"
                        sx={cancelButtonStyles}
                        onClick={handleCancel}
                        disabled={cancelPurchase.isPending}
                    >
                        {translate(ResourcesIds.PURCHASE_CANCEL)}
                    </Button>
                )}
            </Box>

            {/* Its own row under the sessions' actions rather than among them: the buttons
                above are about what the patient bought, this is about the piece of paper the
                clinic owes them for it. */}
            <PurchaseInvoiceActions
                purchase={purchase}
                invoice={props.invoice}
                patientName={props.patientName}
                patientEmail={props.patientEmail}
                onChanged={props.onInvoiceChanged}
            />

            {expanded && (
                <Box sx={sessionListStyles}>
                    {isLoadingSessions ? (
                        <CircularProgress size={20} />
                    ) : !detail || detail.appointments.length === 0 ? (
                        <Typography variant="body2" sx={sessionTherapistStyles}>
                            {translate(ResourcesIds.PURCHASE_SESSIONS_NONE)}
                        </Typography>
                    ) : (
                        /* Every appointment, not only the held ones: a no-show is part of
                           how a course of treatment went, and hiding it would make a
                           package that took twice as long look uneventful. */
                        detail.appointments.map(appointment => (
                            <Box key={appointment.id} sx={sessionRowStyles}>
                                <Box>
                                    <Typography component="span" sx={sessionDateStyles}>
                                        {formatRecordDateTime(appointment.startsAt, i18n)}
                                        {' · '}
                                        {translate(ResourcesIds.PURCHASE_SESSION_DURATION, {
                                            minutes: purchase.durationMinutes,
                                        })}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={translate(STATUS_LABELS[appointment.status])}
                                        color={STATUS_COLOURS[appointment.status]}
                                        sx={statusChipStyles}
                                    />
                                    {appointment.therapistName && (
                                        <Typography component="div" sx={sessionTherapistStyles}>
                                            {appointment.therapistName}
                                        </Typography>
                                    )}
                                </Box>

                                <Tooltip title={translate(ResourcesIds.PURCHASE_UNDO_SESSION)}>
                                    <IconButton
                                        size="small"
                                        aria-label={translate(ResourcesIds.PURCHASE_UNDO_SESSION)}
                                        onClick={() => handleDeleteSession(appointment.id)}
                                        disabled={deleteSession.isPending}
                                    >
                                        <DeleteOutlineOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))
                    )}
                </Box>
            )}

            <RecordSessionDialog
                open={isRecording}
                purchase={purchase}
                isSaving={recordSession.isPending}
                onClose={() => setIsRecording(false)}
                onConfirm={handleRecordSession}
            />

            {props.canAdjustUsedBefore && (
                <AdjustSessionsUsedBeforeDialog
                    open={isAdjusting}
                    purchase={purchase}
                    onClose={() => setIsAdjusting(false)}
                    onSaved={handleUsedBeforeSaved}
                />
            )}
        </Paper>
    );
};
